import React, { useEffect, useRef } from "react";
import useMobileBreakpoint from "@arctheme-hooks/useMobileBreakpoint";
import { Button } from "@project-stories/elements/Button/Button";
import { ArticleTitle } from "./ArticleTitle";
import { ArticleExcerpt } from "./ArticleExcerpt";
import { ArticleDate } from "./ArticleDate";
import { ArticleAuthor } from "./ArticleAuthor";
import { ArticleReadTime } from "./ArticleReadTime";
import { ArticleTags } from "./ArticleTags";
import { Image } from "@project-stories/elements/Image/Image";
import "./BlogArticleTile.scss";

declare global {
  interface Window {
    ArticleTiles?: any[];
    shopify?: any;
  }
}

export const BlogArticleTile = ({
  article,
  settings,
  showExcerpt = false,
  showTitle = true,
  showImage = true,
  showTags = false,
  showReadTime = false,
  showAuthor = true,
  showPublishedDate = true,
  showCategory = false,
  showMetadata = false,
  showLink = true,
  type = 'default'
}) => {
  const isMobile = useMobileBreakpoint(settings.globals.responsive_breakpoints.mobile_max);

    // Find the appropriate tile configuration
  const findTileConfig = () => {
    if (!window?.ArticleTiles || window.ArticleTiles.length === 0) {
      return { layout: [], type: 'default', clickableArea: 'image' };
    }

    // If a specific type is provided, find matching tile config
    if (type && type !== 'default') {
      const matchingTile = window.ArticleTiles.find((tile) => tile.type === type);
      if (matchingTile) {
        return { 
          layout: matchingTile.layout || [], 
          type: matchingTile.type,
          clickableArea: matchingTile.clickableArea || 'image'
        };
      }
    }

    // Default to first tile configuration
    const defaultTile = window.ArticleTiles[0];

    return { 
      layout: defaultTile.layout || [], 
      type: defaultTile.type || 'default',
      clickableArea: defaultTile.clickableArea || 'image'
    };
  };

  const tileConfig = findTileConfig();
  const layout = tileConfig.layout;
  const tileType = tileConfig.type;
  const clickableArea = tileConfig.clickableArea;

   const articleTileId = useRef(
      `article-tile-${article?.id || 'unknown'}-${Math.random().toString(36).substr(2, 9)}`
    ).current;

  const injectCSS = (css: string, id: string) => {
    // Only inject if CSS is not empty
    if (css.trim()) {
      const style = document.createElement('style');
      style.id = id;
      style.textContent = css;
      document.head.appendChild(style);
    }
  };

  const parseMinHeightsString = (minHeightsString: string, articleTileId: string) => {
    if (!minHeightsString) return;
    
    // Use a global style ID for all product tiles to consolidate CSS
    const styleId = `article-tile-parsed-heights-global--${tileType}`;
    
    // Get existing CSS from the style tag if it exists
    const existingStyle = document.getElementById(styleId);
    let existingCSS = existingStyle ? existingStyle.textContent || '' : '';
    
    // Split by newlines to get individual breakpoint:height pairs
    const minHeightsPairs = minHeightsString.split('\n').filter(line => line.trim());
    
    // Create a Set to track unique media queries (normalized for comparison)
    const existingQueries = new Set();
    
    // Parse existing CSS to extract media queries
    if (existingCSS) {
      const existingMediaQueries = existingCSS.match(/@media[^}]*}[^}]*}/g) || [];
      existingMediaQueries.forEach(query => {
        // Normalize whitespace for comparison
        const normalized = query.replace(/\s+/g, ' ').trim();
        existingQueries.add(normalized);
      });
    }
    
    let newCSS = '';
    minHeightsPairs.forEach(pair => {
      const [breakpoint, heightValue] = pair.split(':').map(s => s.trim());
      
      if (breakpoint && heightValue) {
        // Parse height value to extract number and unit
        const heightMatch = heightValue.match(/^(\d+(?:\.\d+)?)(.*)$/);
        if (heightMatch) {
          const numberPart = heightMatch[1];
          const unitPart = heightMatch[2] || 'px'; // Default to px if no unit
          
          const mediaQuery = `@media (max-width: ${breakpoint}px) { .product-images-container__${tileType} { min-height: ${numberPart}${unitPart} !important; } }`;
          
          // Normalize for comparison
          const normalizedQuery = mediaQuery.replace(/\s+/g, ' ').trim();
          
          // Only add if this exact media query doesn't already exist
          if (!existingQueries.has(normalizedQuery)) {
            existingQueries.add(normalizedQuery);
            newCSS += `
              @media (max-width: ${breakpoint}px) {
                .article-image-container__${tileType} {
                  min-height: ${numberPart}${unitPart} !important;
                }
              }
              `;
          }
        }
      }
    });

    // Inject the CSS
    injectCSS(newCSS, styleId);

    return styleId;
  };

  const dispatchComponentEvent = (actions, detail) => {
    const actionIds = actions.split(',');

    actionIds.forEach((actionId) => {
      const trimmedActionId = actionId.trim();
      window.dispatchEvent(new CustomEvent(trimmedActionId, { detail }));
    });
  };

  const charLimit = 90;
  const truncateText = (text, charLimit) => {
      return text.length <= charLimit ? text : text.substring(0, charLimit) + "...";
  };

  const getArticleUrl = () => {
      let articleUrl = `/blogs/${article.handle}`;
      if (article.blog?.handle && !article.handle.includes(article.blog.handle)) {
          articleUrl = `/blogs/${article.blog.handle}/${article.handle}`;
      } else if (settings?.blog?.handle && !article.handle.includes(settings.blog.handle)) {
          articleUrl = `/blogs/${settings.blog.handle}/${article.handle}`;
      }
      return articleUrl;
  };
  const articleUrl = getArticleUrl();

  let category = "";
  if (showCategory && article?.tags?.length) {
      const tag = article.tags.find((item) => item.includes("category:"));
      if (tag) category = tag.split("category:")[1];
  }

  const componentMap = {
    ArticleImage: ({ article, blockSettings }) => {
      // Only render if showImage is true
      if (!showImage) return null;
      
      // Handle min heights parsing when blockSettings.min_heights changes
      useEffect(() => {
        if (blockSettings?.min_heights) {
          parseMinHeightsString(blockSettings.min_heights, articleTileId);
        }
      }, [blockSettings?.min_heights]);

      const imageRoundingClass = blockSettings.image_border_radius > 0 ? `rounded-[${blockSettings.image_border_radius}px]` : '';

      const imageContent = (
        <Image
          imageUrl={article.image}
          fit={blockSettings.image_fit}
          ratio={blockSettings.image_aspect_ratio}
          classes={`${blockSettings.classes} ${imageRoundingClass}`}
          width={article.image?.width}
          height={article.image?.height}
        />
      );

      return (
        <div className={`article-image-container__${tileType}`}>
          {clickableArea === 'image' ? (
            <a href={articleUrl} className="block">
              {imageContent}
            </a>
          ) : (
            imageContent
          )}
        </div>
      );
    },
    ArticleTitle: ({ article, blockSettings }) => {
      // Only render if showTitle is true
      if (!showTitle) return null;
      
      return (
        <ArticleTitle
          article={article}
          settings={blockSettings}
        />
      );
    },
    ArticleExcerpt: ({ article, blockSettings }) => {
      // Only render if showExcerpt is true
      if (!showExcerpt) return null;
      
      return (
        <ArticleExcerpt
          article={article}
          settings={blockSettings}
        />
      );
    },
    ArticleDate: ({ article, blockSettings }) => {
      // Only render if showPublishedDate is true
      if (!showPublishedDate) return null;
      
      return (
        <ArticleDate
          article={article}
          settings={blockSettings}
        />
      );
    },
    ArticleAuthor: ({ article, blockSettings }) => {
      // Only render if showAuthor is true
      if (!showAuthor) return null;
      
      return (
        <ArticleAuthor
          article={article}
          settings={blockSettings}
        />
      );
    },
    ArticleReadTime: ({ article, blockSettings }) => {
      // Only render if showReadTime is true
      if (!showReadTime) return null;
      
      return (
        <ArticleReadTime
          article={article}
          settings={blockSettings}
        />
      );
    },
    ArticleTags: ({ article, blockSettings, globals, isMobile }) => {
      // Only render if showTags is true
      if (!showTags) return null;
      
      return (
        <ArticleTags
          article={article}
          settings={blockSettings}
          globals={globals}
          isMobile={isMobile}
        />
      );
    },
    ArticleLink: ({ article, blockSettings, globals, isMobile }) => {
      // Only render if showLink is true AND clickableArea is not 'whole-tile'
      if (!showLink || clickableArea === 'whole-tile') return null;
      
      return (
        <Button
          settings={settings}
          label={blockSettings.link_text || 'Read More'}
          link={article.url}
          style={blockSettings.link_style || 'primary'}
          styleType={blockSettings.cta_style_type || 'link'}
          icon={blockSettings.link_icon}
          reverse={blockSettings.link_reverse_icon || false}
          fullWidth={blockSettings.link_full_width || false}
          className={blockSettings.link_classes || ''}
        />
      );
    },
    Group: ({ block, settings, children, ...props }) => {
      const groupSettings = block?.settings || {};
      const getSizeClasses = () => {
        let classes: string[] = [];

        // Width handling
        if (groupSettings.use_specific_width) {
          const width = groupSettings.specific_width || 100;
          if (width < 100) {
            classes.push(`w-[${width}%]`);
          } else {
            classes.push('w-full');
          }
        } else {
          const flexBasis = groupSettings.flex_basis || 100;
          if (flexBasis < 100) {
            classes.push(`basis-[${flexBasis}%]`);
          } else {
            classes.push('basis-[100%]');
          }
        }

        // Max width
        if (groupSettings.max_width > 0) {
          classes.push(`max-w-[${groupSettings.max_width}px]`);
        }

        // Block alignment
        if (groupSettings.alignment === 'center') {
          classes.push('mx-auto');
        } else if (groupSettings.alignment === 'end') {
          classes.push('ml-auto mr-0');
        }

        return classes.join(' ');
      };

      const getBackgroundStyles = () => {
        if (!groupSettings.background_enabled || !groupSettings.background_color) {
          return {};
        }

        const opacity = (groupSettings.background_opacity || 100) / 100;
        return {
          backgroundColor: groupSettings.background_color,
          opacity: opacity,
          borderRadius: `${groupSettings.background_radius || 0}px`
        };
      };

      const groupClasses = [
        'group-block',
        `color-${groupSettings.color_scheme || 'scheme-1'}`,
        getSizeClasses(),
        groupSettings.background_enabled ? `rounded-[${groupSettings.background_radius || 0}px]` : '',
        groupSettings.classes || ''
      ]
        .filter(Boolean)
        .join(' ');

      const contentClasses = ['group-blocks group-blocks-container !relative h-full'].join(' ');

      // Animation attributes
      const animationAttrs = groupSettings.enable_animations
        ? {
            'data-aos': groupSettings.animation_type || 'slide-fade-up',
            'data-aos-duration': groupSettings.animation_duration || 800,
            'data-aos-delay': groupSettings.animation_delay || 0,
            'data-aos-trigger': groupSettings.animation_trigger || 'onscroll'
          }
        : {};

      // Collapsible attributes for Alpine.js
      const collapsibleAttrs = groupSettings.enable_collapsible
        ? {
            'x-data': `{ isCollapsed: ${groupSettings.collapsed_by_default || false} }`,
            ...(groupSettings.target_id
              ? {
                  'x-on:toggle-collapse-window': 'isCollapsed = !isCollapsed',
                  id: groupSettings.target_id
                }
              : {})
          }
        : {};

      // If this group has nested layout items, render them
      const renderNestedBlocks = () => {
        if (block?.layout && block.layout.length > 0) {
          return block.layout.map((nestedBlock, nestedIndex) =>
            renderComponent(nestedBlock, `${block.id || 'group'}-${nestedIndex}`)
          );
        }
        return children;
      };

      return (
        <div
          className={`responsive-${block?.id} ${groupClasses}`}
          style={{
            ...getBackgroundStyles()
          }}
          data-block-id={block?.id}
          data-block-type='group'
          data-horizontal-align={groupSettings.content_horizontal_align}
          data-vertical-align={groupSettings.content_vertical_align}
          {...animationAttrs}
          {...(groupSettings.enable_collapsible ? collapsibleAttrs : {})}
        >
          {/* Background overlay for opacity control */}
          {groupSettings.background_enabled && groupSettings.background_color && (
            <div
              className={`absolute inset-0 pointer-events-none rounded-[${groupSettings.background_radius || 0}px]`}
              style={{
                backgroundColor: groupSettings.background_color,
                opacity: (groupSettings.background_opacity || 100) / 100
              }}
            />
          )}

          {/* Built-in Toggle Button */}
          {groupSettings.enable_collapsible && groupSettings.show_toggle_button && (
            <div
              className={`collapsible-toggle-wrapper relative z-10 flex mb-4 ${
                groupSettings.toggle_position === 'right'
                  ? 'justify-end'
                  : groupSettings.toggle_position === 'center'
                  ? 'justify-center'
                  : 'justify-start'
              }`}
            >
              <button
                onClick={() => {
                  // Dispatch component event
                  dispatchComponentEvent('GroupToggle', { action: 'toggle_click', groupId: block?.id });

                  // Handle toggle logic - this would need to be connected to Alpine.js or React state
                  console.log('Toggle collapsed state');
                }}
                className={`collapsible-toggle-btn flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                  groupSettings.toggle_style === 'primary'
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : groupSettings.toggle_style === 'secondary'
                    ? 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                    : 'bg-transparent border-2 border-gray-300 text-gray-700 hover:border-gray-400'
                }`}
              >
                <span className='toggle-text'>{groupSettings.toggle_text || 'Toggle Content'}</span>

                {/* Toggle Icons */}
                {groupSettings.toggle_icon_style === 'plus-minus' && (
                  <>
                    <span className='toggle-icon text-lg'>+</span>
                    {/* Note: In actual implementation, this would be conditional based on collapsed state */}
                  </>
                )}
                {groupSettings.toggle_icon_style === 'chevron' && (
                  <svg
                    className='w-4 h-4 transition-transform'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='2'
                      d='M19 9l-7 7-7-7'
                    ></path>
                  </svg>
                )}
                {groupSettings.toggle_icon_style === 'arrow' && (
                  <svg
                    className='w-4 h-4 transition-transform'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='2'
                      d='M13 7l5 5-5 5M6 12h12'
                    ></path>
                  </svg>
                )}
              </button>
            </div>
          )}

          {/* Content Container */}
          <div
            className={`groups-blocks-container ${contentClasses}`}
            style={
              groupSettings.enable_collapsible
                ? {
                    display: groupSettings.collapsed_by_default ? 'none' : 'block'
                    // Note: In actual implementation, this would be controlled by Alpine.js or React state
                  }
                : {}
            }
          >
            {renderNestedBlocks()}
          </div>
        </div>
      );
    }
  };

  const renderComponent = (layoutItem, index) => {
    const Component = componentMap[layoutItem.name];

    if (!Component) {
      console.warn(`Component ${layoutItem.name} not found in componentMap`);
      return null;
    }

    const componentProps = {
      article,
      settings,
      globals: settings.globals,
      isMobile,
      block: layoutItem, // Pass the layout item as block for Group components
      blockSettings: layoutItem.settings, // Merge any settings from the layout item
    };

    return (
      <Component
        key={index}
        {...componentProps}
      />
    );
  };

    const tileContent = (
      <div
        className={`blog-article-tile ${clickableArea === 'whole-tile' ? 'cursor-pointer' : ''}`}
        id={articleTileId}
      >
        {layout.map((layoutItem, index) => renderComponent(layoutItem, index))}
      </div>
    );

    return clickableArea === 'whole-tile' ? (
      <a href={articleUrl} className="block no-underline text-inherit hover:text-inherit">
        {tileContent}
      </a>
    ) : (
      tileContent
    );
};

export default BlogArticleTile;
